/**
 * Kid exits — active one-shot permissions the desk honors. Confirming
 * consumes the permission and pushes the flat ("just left the gate").
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { str, useGateList } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Screen, Skeleton, TopBar } from '@/ui';

type Exit = { id: string; kid: string; flat: string; until: string };

function toExit(k: unknown, i: number): Exit {
  const iso = str(k, 'expiresAt') ?? '';
  const d = new Date(iso);
  let until = '';
  if (!Number.isNaN(d.getTime())) {
    let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
    until = `${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
  }
  return {
    id: str(k, 'id') ?? `k${i}`,
    kid: str(k, 'kidName') ?? 'Child',
    flat: str(k, 'flatCode') ?? '',
    until,
  };
}

export default function GuardKidExits() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state, reload } = useGateList<unknown>('/api/gate/kid-exits', 'exits');
  const rows = items.map(toExit);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const confirm = async (e: Exit) => {
    setBusyId(e.id);
    try {
      await api.post<unknown>(`/api/gate/kid-exits/${encodeURIComponent(e.id)}/confirm`, {});
      await reload();
    } catch { /* transient */ } finally {
      setBusyId(null);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('kids_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <AppText variant="bodySm" tone="tertiary" style={{ marginBottom: space.s4 }}>
          {t('kids_intro')}
        </AppText>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1].map((i) => <Skeleton key={i} h={72} />)}</View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="clock" size={40} color={colors.textTertiary} />}
            title={t('kids_empty_title')}
            body={t('kids_empty_body')}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {rows.map((e) => (
              <Card key={e.id} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" weight="600">{e.kid}</AppText>
                  <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                    {e.flat}{e.until ? ` · ${t('kids_until', { time: e.until })}` : ''}
                  </AppText>
                </View>
                <Button
                  title={busyId === e.id ? t('kids_confirming') : t('kids_confirm')}
                  kind="secondary"
                  onPress={() => void confirm(e)}
                />
              </Card>
            ))}
          </View>
        )}
      </Screen>
    </Screen>
  );
}
